import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import styles from './Users.module.scss';
const formatPhoneNumber = (phone) => {
    if (!phone)
        return '';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Return original if not a valid length
    if (digits.length !== 10)
        return phone;
    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
export const createUsersTableColumns = (handleEdit, handleDelete, handlePasswordReset, handleResendVerification, currentUser) => {
    return [
        {
            key: 'name',
            header: 'Name',
            accessor: 'first_name',
            render: (row) => {
                const isCurrentUser = currentUser && (row.id === currentUser.id || row.email === currentUser.email);
                const firstName = row.first_name || '';
                const lastName = row.last_name || '';
                const displayName = firstName && lastName ? `${firstName} ${lastName}` :
                    firstName || lastName || row.username || row.name;
                return (_jsx("div", { children: _jsxs("div", { style: { fontWeight: 600 }, children: [displayName, isCurrentUser && (_jsx("span", { style: {
                                    marginLeft: '8px',
                                    fontSize: '12px',
                                    fontWeight: 'normal',
                                    color: '#10B981',
                                    backgroundColor: '#D1FAE5',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                }, children: "You" }))] }) }));
            },
        },
        {
            key: 'email',
            header: 'Email Address',
            accessor: 'email',
            render: (row) => (_jsx("a", { href: `mailto:${row.email}`, style: { color: '#078FF0', textDecoration: 'none' }, onClick: (e) => e.stopPropagation(), children: row.email })),
        },
        {
            key: 'phone',
            header: 'Phone Number',
            accessor: 'phone',
            render: (row) => {
                const phoneNumber = row.phone || row.primary_clinic?.phone;
                return (_jsx("span", { children: phoneNumber ? formatPhoneNumber(phoneNumber) : '-' }));
            },
        },
        {
            key: 'clinic',
            header: 'Clinic Name',
            accessor: 'primary_clinic',
            render: (row) => (_jsx("div", { children: row.primary_clinic ? row.primary_clinic.name : '-' })),
        },
        {
            key: 'role',
            header: 'Role',
            accessor: 'role',
            render: (row) => row.role ? row.role.charAt(0).toUpperCase() + row.role.slice(1).toLowerCase() : '',
        },
        {
            key: 'actions',
            header: 'Action',
            render: (user) => {
                const UserActionMenu = () => {
                    const [isOpen, setIsOpen] = useState(false);
                    const [openUpward, setOpenUpward] = useState(false);
                    const menuRef = useRef(null);
                    const buttonRef = useRef(null);
                    const dropdownRef = useRef(null);
                    const updateMenuDirection = useCallback(() => {
                        if (!isOpen || !buttonRef.current || !dropdownRef.current)
                            return;
                        const triggerRect = buttonRef.current.getBoundingClientRect();
                        const dropdownHeight = dropdownRef.current.scrollHeight;
                        const viewportPadding = 8;
                        const spaceBelow = window.innerHeight - triggerRect.bottom;
                        const spaceAbove = triggerRect.top;
                        setOpenUpward(spaceBelow < dropdownHeight + viewportPadding && spaceAbove > spaceBelow);
                    }, [isOpen]);
                    useEffect(() => {
                        const handleClickOutside = (event) => {
                            if (menuRef.current && !menuRef.current.contains(event.target)) {
                                setIsOpen(false);
                            }
                        };
                        document.addEventListener('mousedown', handleClickOutside);
                        return () => document.removeEventListener('mousedown', handleClickOutside);
                    }, []);
                    useLayoutEffect(() => {
                        if (!isOpen)
                            return;
                        updateMenuDirection();
                        window.addEventListener('resize', updateMenuDirection);
                        window.addEventListener('scroll', updateMenuDirection, true);
                        return () => {
                            window.removeEventListener('resize', updateMenuDirection);
                            window.removeEventListener('scroll', updateMenuDirection, true);
                        };
                    }, [isOpen, updateMenuDirection]);
                    return (_jsxs("div", { className: styles.actionMenu, ref: menuRef, onClick: (e) => e.stopPropagation(), children: [_jsx("button", { ref: buttonRef, onClick: () => setIsOpen(!isOpen), className: styles.actionButton, "aria-expanded": isOpen, "aria-haspopup": "menu", children: "\u22EF" }), isOpen && (_jsxs("div", { ref: dropdownRef, className: `${styles.actionDropdown} ${openUpward ? styles.actionDropdownUp : ''}`, children: [_jsx("button", { onClick: () => {
                                            handlePasswordReset(user);
                                            setIsOpen(false);
                                        }, className: styles.actionItem, children: "Send Password Reset" }), _jsx("button", { onClick: () => {
                                            handleResendVerification(user);
                                            setIsOpen(false);
                                        }, className: styles.actionItem, children: "Resend Verification Email" }), _jsx("button", { onClick: () => {
                                            handleEdit(user);
                                            setIsOpen(false);
                                        }, className: styles.actionItem, children: "Edit User Details" }), _jsx("button", { onClick: () => {
                                            handleDelete(user);
                                            setIsOpen(false);
                                        }, className: styles.actionItem, disabled: currentUser &&
                                            (user.id === currentUser.id || user.email === currentUser.email), style: {
                                            opacity: currentUser &&
                                                (user.id === currentUser.id || user.email === currentUser.email)
                                                ? 0.5
                                                : 1,
                                            cursor: currentUser &&
                                                (user.id === currentUser.id || user.email === currentUser.email)
                                                ? 'not-allowed'
                                                : 'pointer',
                                        }, children: "Delete User" })] }))] }));
                };
                return _jsx(UserActionMenu, {});
            },
        },
    ];
};
